import { createSlice, Dispatch, PayloadAction } from "@reduxjs/toolkit";
import type { ContentTypeConfig } from "../../API/Models";
import { client } from "../../Clients";

/**
 * Content-type CONFIG rows (the DB entities behind /content-types : display config, flags), as
 * opposed to ContentTypesSlice which caches the schemas themselves. Deliberately parallel to
 * CategoriesSlice : same loading/isRefreshing/initialized shape and fetch(manual) thunk; see the
 * comment there. Kept as its own slice so the exported action names stay stable.
 */
export interface ContentTypeConfigsState {
	items: ContentTypeConfig[];
	loading: boolean;
	isRefreshing: boolean;
	error?: string | null;
	initialized: boolean;
	lastUpdated: string | null;
}

const initialState: ContentTypeConfigsState = {
	items: [],
	loading: false,
	isRefreshing: false,
	error: null,
	initialized: false,
	lastUpdated: null
};

const contentTypeConfigsSlice = createSlice({
	name: "contentTypeConfigs",
	initialState,
	reducers: {
		setLoading(state, action: PayloadAction<boolean>) {
			state.loading = action.payload;
		},
		setRefreshing(state, action: PayloadAction<boolean>) {
			state.isRefreshing = action.payload;
		},
		setContentTypeConfigs(state, action: PayloadAction<ContentTypeConfig[]>) {
			state.items = action.payload ?? [];
			state.initialized = true;
			state.error = null;
			state.lastUpdated = new Date().toISOString();
		},
		setError(state, action: PayloadAction<string | null>) {
			state.error = action.payload;
			state.loading = false;
			state.isRefreshing = false;
		}
	}
});

export const { setLoading, setRefreshing, setContentTypeConfigs, setError } = contentTypeConfigsSlice.actions;
export const { reducer } = contentTypeConfigsSlice;

export const fetchContentTypeConfigs = (manual = false) => async (dispatch: Dispatch) => {
	if (manual) {
		dispatch(setRefreshing(true));
	} else {
		dispatch(setLoading(true));
	}
	try {
		const response = await client.contentTypeConfigs.apiContentTypeConfigsGet();
		dispatch(setContentTypeConfigs(response.data ?? []));
	} catch (err: any) {
		dispatch(setError(err?.message ?? "Unable to load content types"));
	} finally {
		if (manual) {
			dispatch(setRefreshing(false));
		} else {
			dispatch(setLoading(false));
		}
	}
};

